// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { wishListCardStyles } from "@awc/Overrides/Components/WishListCard";
import Star from "@awc/XDL/Icons/Star";
import StarX from "@awc/XDL/Icons/StarX";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { color, ellipsis, moreActionsSharedStyle, xScreen } from "@awc/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

wishListCardStyles.container = {
    css: css`
        display: grid;
        grid-template-columns: minmax(317px, 430px) minmax(0, 317px) minmax(95px, 160px) minmax(165px, 204px);
        grid-column-gap: 22px;
        justify-content: space-between;
        ${xScreen("lte", "md")} {
            grid-template-columns: minmax(317px, 1fr) minmax(0, 1fr);
        }
        ${xScreen("lte", "sm")} {
            grid-template-columns: 1fr;
        }
        grid-row-gap: 12px;
        width: 100%;
    `,
};

// Description Section Styles
wishListCardStyles.descriptionWrapper = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 1 / 2;
        > div {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
    `,
};

wishListCardStyles.nameWrapper = {
    css: css`
        & > ${StyledButton} {
            font-weight: bold;
            ${TypographyStyle} {
                font-size: ${fontConfig.fontSize.regular}px;
                line-height: 22px;
            }
        }
    `,
};

wishListCardStyles.extendedInfoGridItem = {
    width: [12, 12, 7, 6, 6],
    align: "middle",
    css: css`
        align-items: center;
    `,
};

wishListCardStyles.sharingStatusGridItem = {
    width: 12,
    css: css`
        align-items: center;
        font-size: ${fontConfig.fontSize.xSmall}px;
        line-height: 13px;
        gap: 20px;
        ${xScreen("lte", "md")} {
            justify-content: flex-start;
            &:empty {
                display: none;
            }
        }
    `,
};

wishListCardStyles.productImagesGridItem = {
    width: 12,
    css: css`
        justify-content: flex-start;
        align-items: center;
        flex-wrap: wrap;
        gap: 21px;

        ${xScreen("lte", "md")} {
            &:empty {
                display: none;
            }
        }

        ${xScreen("lte", "sm")} {
            padding: 13px 15px 10px;
        }
    `,
};

wishListCardStyles.lastUpdatedText = {
    size: fontConfig.fontSize.xSmall,
    lineHeight: fontConfig.lineHeight.xSmall,
};

wishListCardStyles.descriptionText = {
    size: fontConfig.fontSize.xSmall,
    lineHeight: fontConfig.lineHeight.xSmall,
    css: css`
        ${ellipsis(2)}
    `,
};

wishListCardStyles.actionFavoriteClickable = {
    css: css`
        width: auto;
        font-size: ${fontConfig.fontSize.xSmall}px;
        line-height: ${fontConfig.lineHeight.xSmall};
        color: ${color("text.link")};

        &:hover {
            cursor: pointer;
            color: ${color("common.ui8")};
        }
    `,
};

wishListCardStyles.favTagsWrapper = {
    css: css`
        display: flex;
        column-gap: 24px;
        > div {
            line-height: ${fontConfig.lineHeight.xSmall};
        }
    `,
};

wishListCardStyles.starIcon = {
    src: Star,
    size: 14,
    color: "primary.main",
    css: css`
        margin: 0 6px 2px 0;
        &:hover {
            color: ${color("common.ui8")};
        }
    `,
};

wishListCardStyles.starXIcon = {
    src: StarX,
    size: 14,
    color: "primary.main",
    css: css`
        margin: 0 6px 2px 0;
        &:hover {
            color: ${color("common.ui8")};
        }
    `,
};

wishListCardStyles.tagTooltip = {
    mergeCss: true,
    cssOverrides: {
        tooltipClickable: css`
            display: flex;
            align-items: center;
            font-size: ${fontConfig.fontSize.xSmall}px;
            line-height: ${fontConfig.lineHeight.xSmall};
            margin: 0;
            gap: 6px;
        `,
    },
};

wishListCardStyles.tagIcon = {
    //  src: LinkTag,
    size: 12,
    color: "common.ui4",
    css: css`
        margin-right: 6px;
        position: relative;
        top: 2px;
    `,
};

// Image Section Styles
wishListCardStyles.imagesWrapper = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 1 / 2;
        display: flex;
        gap: 22px;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 2;
            grid-row: 2 / 3;
            gap: 8px;
        }
    `,
};

wishListCardStyles.productImageLink = {
    css: css`
        display: flex;
        align-items: flex-start;
        margin: 0;
        height: fit-content;
    `,
};

wishListCardStyles.productImage = {
    mergeCss: true,
    css: css`
        max-width: 91px;
        max-height: 91px;
        img {
            aspect-ratio: 1;
            object-fit: contain;
        }
    `,
};

// sharing status Wrapper
wishListCardStyles.sharingStatusWrapper = {
    css: css`
        grid-column: 3 / 4;
        grid-row: 1 / 2;
        display: flex;
        align-items: flex-start;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 2;
            grid-row: 3 / 4;
        }
    `,
};

// actions Wrapper
wishListCardStyles.actionsWrapper = {
    css: css`
        grid-column: 4 / 5;
        grid-row: 1 / 2;
        ${xScreen("lte", "md")} {
            grid-column: 2 / 3;
            grid-row: 3 / 4;
        }
        ${xScreen("lte", "sm")} {
            grid-column: 1 / 2;
            grid-row: 4 / 5;
        }
    `,
};

wishListCardStyles.actionGridMediumHidden = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
    `,
};

wishListCardStyles.overflowMenu = {
    ...moreActionsSharedStyle.overflowMenu,
};

wishListCardStyles.moreActions = {
    ...moreActionsSharedStyle.moreActionsLink,
};

wishListCardStyles.sharingStatus = {
    privateTooltip: {
        iconProps: {
            css: css`
                margin-top: 3px;
            `,
        },
    },
};

wishListCardStyles.actionAddToCartButton = {
    css: css`
        white-space: nowrap;
        width: 100%;
        padding: 0 10px;
    `,
};

wishListCardStyles.actionLeaveClickable =
    wishListCardStyles.actionDeleteClickable =
    wishListCardStyles.actionHideClickable =
        {
            css: css`
                &&& {
                    color: ${color("text.main")};
                }
            `,
        };
